import { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useConversationUuid } from '~/hooks/conversation/useConversationUuid'
import { useTaskDetails } from '~/hooks/task/useTaskDetails'
import { useTaskProgressMessages } from '~/hooks/task/useTaskProgressMessages'
import { ModalId, useModalController } from '~/hooks/useModal'
import { AgentTaskStatus, Diff } from '~/protos/agents'
import { bridge } from '~/services/bridge'
import { CollapsibleLogCard, extractProgressMessagesFromDB, FileDropdown } from '../common'

interface SourceFilesLoadingSlideProps {
  taskStatus?: AgentTaskStatus
}

export function SourceFilesLoadingSlide({ taskStatus }: SourceFilesLoadingSlideProps) {
  const isPlugin = bridge.isInPlugin()
  const [searchParams] = useSearchParams()
  const conversationUuid = useConversationUuid()
  const { openModal } = useModalController(ModalId.RefactorFileViewer)

  const taskId = searchParams.get('task_id') ?? ''
  const { data: taskDetails } = useTaskDetails(conversationUuid, taskId)

  // Loading state - waiting for endpoints to be selected and source files to be generated
  const isLoading = taskStatus !== AgentTaskStatus.REFACTOR_EXISTING_DOCS_GENERATED

  // Fetch progress messages from the database - uses WebSocket for real-time updates
  const { data: dbProgressMessages } = useTaskProgressMessages(conversationUuid, taskId, {
    enabled: !!conversationUuid && !!taskId
  })

  // Extract progress messages for the current state using the new DB-backed messages
  const progressMessages = useMemo(
    () => extractProgressMessagesFromDB(dbProgressMessages, taskStatus),
    [dbProgressMessages, taskStatus]
  )

  // Extract migration diffs from task data - check both current streaming state and completed state
  // This allows diffs to update incrementally as they stream in
  const migrationDiffs = useMemo(() => {
    if (!taskDetails?.task?.details?.orderedStateResponses) {
      return []
    }

    const tuples = taskDetails.task.details.orderedStateResponses

    // First, try to find diffs from the completed state (REFACTOR_EXISTING_DOCS_GENERATED)
    for (let i = tuples.length - 1; i >= 0; i--) {
      const tuple = tuples[i]
      if (tuple.taskStatus === AgentTaskStatus.REFACTOR_EXISTING_DOCS_GENERATED) {
        const diffs = tuple.response?.[0]?.responseContentStream?.diff || []
        if (diffs.length > 0) {
          return diffs
        }
        break
      }
    }

    // If not found, try to find diffs from the current streaming state (MIGRATION_ENDPOINTS_SELECTED)
    for (let i = tuples.length - 1; i >= 0; i--) {
      const tuple = tuples[i]
      if (tuple.taskStatus === AgentTaskStatus.MIGRATION_ENDPOINTS_SELECTED) {
        const diffs = tuple.response?.[0]?.responseContentStream?.diff || []
        if (diffs.length > 0) {
          return diffs
        }
        break
      }
    }

    return []
  }, [taskDetails])

  // Open the file viewer modal at the file the user actually clicked.
  // The signature uses an options object to defeat TypeScript's function
  // parameter bivariance — a single-arg variant would type-check and
  // silently drop ``activePath``, sending users to the default first
  // file every time.
  const handleFileClick = ({ activePath }: { diffs: Diff[]; activePath: string }) => {
    if (migrationDiffs.length > 0) {
      openModal({
        diffs: migrationDiffs,
        initialActivePath: activePath
      })
    }
  }

  return (
    <div className='w-full h-full overflow-y-auto'>
      <div className={`flex justify-center w-full ${isPlugin ? 'px-3' : 'px-[60px]'}`}>
        <div
          className={`w-full flex flex-col ${isPlugin ? 'max-w-full pb-6' : 'max-w-[720px] pb-[60px]'}`}
        >
          {/* Modal Header */}
          <div className='flex flex-col gap-[16px] items-center pt-[60px]'>
            <p className="font-['Geist'] font-bold text-[20px] leading-[22px] text-black text-center">
              {'Code Documentation'}
            </p>
            <p className="font-['Geist'] font-medium text-[14px] leading-[22px] text-[rgba(0,0,0,0.65)] text-center">
              {isLoading
                ? 'We are generating the code documentation. This may take a while.'
                : 'We have generated the Code Documentation. Feel free to review and download to better understand your code that you wish to refactor.'}
            </p>
          </div>

          {/* Content */}
          <div className='flex flex-col gap-[24px] items-center w-full mt-[36px]'>
            {/* Agent Progress Logs */}
            <CollapsibleLogCard
              messages={progressMessages}
              title='Agent Progress'
              isComplete={taskStatus === AgentTaskStatus.REFACTOR_EXISTING_DOCS_GENERATED}
            />

            {/* Documentation Files - Always show the card, even during loading */}
            <FileDropdown
              title='Documentation'
              icon='checkCircle'
              files={migrationDiffs}
              onFileClick={handleFileClick}
              scrollAfterFiles={5}
              iconColor='#65558f'
              titleColor='#65558f'
              isComplete={!isLoading}
              defaultExpanded={!isLoading}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
